import React, { useState, useEffect } from "react";
import "./MiniCalendar.css";

/* ===== Base API robuste (prod & dev) ===== */
const RAW =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) ||
  process.env.VITE_API_URL ||
  process.env.REACT_APP_API_URL ||
  "";

const API_BASE =
  RAW
    ? RAW.replace(/\/+$/, "").replace(/\/api$/i, "")
    : (typeof window !== "undefined" && window.location.origin) || "";

// ===== Réglages compacts =====
const VISIBLE_DAYS = 5; // 5 jours visibles
const DEFAULT_ROWS = 4; // 4 créneaux visibles par défaut
const FIRST_COL_PX = 86; // largeur colonne "Horaires" (px)

/* Largeur fixe par colonne jour en App/iOS pour éviter le bug Safari */
const DAY_COL_PX_APP = 96;

function MiniCalendar({ proId: propProId, annonceId, profilId, onSlotClick, isApp = false }) {
  const [place, setPlace] = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [blockSlots, setBlockSlots] = useState([]);
  const [windowStart, setWindowStart] = useState(startOfDay(new Date()));
  const [showMore, setShowMore] = useState(false);

  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  function startOfDay(d) {
    const n = new Date(d);
    n.setHours(0, 0, 0, 0);
    return n;
  }
  function addDays(d, n) {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
  }
  const safeSplit = (s, sep = ":") => (typeof s === "string" ? s.split(sep) : []);
  const parseIntSafe = (v, d = 0) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : d;
  };
  const setDateFromHHMM = (base, hhmm) => {
    if (typeof hhmm !== "string" || !hhmm.includes(":")) return null;
    const [h, m] = safeSplit(hhmm).map((x) => parseIntSafe(x));
    const d = new Date(base);
    d.setHours(h, m, 0, 0);
    return d;
  };
  const normalizeWorkDay = (dayObj) => {
    if (!dayObj || dayObj.isOpen === false) return { isOpen: false, slots: [] };
    if (Array.isArray(dayObj.slots) && dayObj.slots.length > 0) {
      const slots = dayObj.slots.filter((s) => s && s.start && s.end);
      return { isOpen: true, slots };
    }
    if (dayObj.start || dayObj.end) {
      return { isOpen: true, slots: [{ start: dayObj.start || "00:00", end: dayObj.end || "23:59" }] };
    }
    return { isOpen: false, slots: [] };
  };
  const shortDayLabel = (date) => {
    const s = date.toLocaleDateString("fr-FR", { weekday: "short" }).replace(/\.$/, "");
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const proIdEffectif = propProId || (place?.userId && place.userId._id) || place?.userId || null;

  useEffect(() => {
    async function fetchData() {
      try {
        const common = {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: "include",
        };

        if (annonceId) {
          const res = await fetch(`${API_BASE}/api/annonces/${annonceId}`, common);
          const annonce = await res.json();
          setPlace(annonce);
        } else if (propProId) {
          const res = await fetch(`${API_BASE}/api/annonces/by-user/${propProId}`, common);
          const annonces = await res.json();
          setPlace(Array.isArray(annonces) ? annonces[0] || null : annonces || null);
        }
      } catch {
        setPlace(null);
      }
    }
    fetchData();
  }, [annonceId, propProId, token]);

  useEffect(() => {
    if (!proIdEffectif) return;
    async function fetchSlots() {
      try {
        let url = `${API_BASE}/api/slotsCalendar/pro/${proIdEffectif}`;
        if (profilId) url += `?profilId=${profilId}`;
        const res = await fetch(url, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: "include",
        });
        let data = await res.json();
        if (!Array.isArray(data)) data = [];

        const booked = [];
        const blocks = [];
        for (const s of data) {
          const start = new Date(s.start);
          const end = new Date(s.end);
          if (s.type === "BLOCK") {
            blocks.push({ start, end, id: s._id || s.id, reason: s.reason || "" });
          } else {
            booked.push({ start, end, id: s._id || s.id });
          }
        }
        setBookedSlots(booked);
        setBlockSlots(blocks);
      } catch {
        setBookedSlots([]);
        setBlockSlots([]);
      }
    }
    fetchSlots();
  }, [proIdEffectif, profilId, windowStart, token]);

  function getWorkingHours() {
    if (profilId && place && Array.isArray(place.profils)) {
      const profil = place.profils.find((p) => String(p._id) === String(profilId));
      if (profil && Array.isArray(profil.workingHours) && profil.workingHours.length > 0) {
        return profil.workingHours;
      }
    }
    return place?.workHours || [];
  }

  function getActivityDuration() {
    if (profilId && place && Array.isArray(place.profils)) {
      const profil = place.profils.find((p) => String(p._id) === String(profilId));
      if (profil && Array.isArray(profil.activities) && profil.activities.length > 0) {
        const act = profil.activities[0];
        const minutes = parseIntSafe(act.durationHours) * 60 + parseIntSafe(act.durationMinutes);
        return minutes > 0 ? minutes : 30;
      }
    }
    return 30;
  }

  function slotIsWithinWorkingHours(slotStart, slotEnd, rawWorkDay) {
    const { isOpen, slots } = normalizeWorkDay(rawWorkDay);
    if (!isOpen || slots.length === 0) return false;
    return slots.some((seg) => {
      const s = setDateFromHHMM(slotStart, seg?.start);
      const e = setDateFromHHMM(slotStart, seg?.end);
      if (!s || !e) return false;
      return slotStart >= s && slotEnd <= e;
    });
  }

  const generateSlotsForDay = (dayName) => {
    const wh = getWorkingHours();
    const rawDay = wh.find((d) => (d?.day || "").toLowerCase().startsWith(dayName.toLowerCase()));
    const { isOpen, slots } = normalizeWorkDay(rawDay);
    if (!isOpen || slots.length === 0) return [];

    const slotInterval = 30;
    const times = new Set();

    for (const seg of slots) {
      const [sh, sm] = safeSplit(seg.start).map((x) => parseIntSafe(x));
      const [eh, em] = safeSplit(seg.end).map((x) => parseIntSafe(x));
      let h = sh,
        m = sm;
      while (h < eh || (h === eh && m < em)) {
        times.add(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
        m += slotInterval;
        if (m >= 60) {
          m -= 60;
          h++;
        }
      }
    }

    return Array.from(times).sort((a, b) => {
      const [ah, am] = safeSplit(a).map((x) => parseIntSafe(x));
      const [bh, bm] = safeSplit(b).map((x) => parseIntSafe(x));
      return ah * 60 + am - (bh * 60 + bm);
    });
  };

  const isSlotPast = (dateObj) => dateObj < new Date();

  function handleSlotClick(date) {
    if (isSlotPast(date)) return;
    if (onSlotClick) onSlotClick(date);
  }

  const handlePrev = () => setWindowStart((d) => addDays(d, -VISIBLE_DAYS));
  const handleNext = () => setWindowStart((d) => addDays(d, VISIBLE_DAYS));

  const rootCls = `mini-calendar${isApp ? " mini-calendar--app" : ""}`;
  const headCls = `calendar-head${isApp ? " calendar-head--app" : ""}`;
  const prevBtnCls = `nav-btn${isApp ? " nav-btn--app" : ""}`;
  const nextBtnCls = `nav-btn${isApp ? " nav-btn--app" : ""}`;
  const moreBtnCls = `voir-plus-btn${isApp ? " voir-plus-btn--app" : ""}`;

  const IS_IOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);

  const renderCalendar = () => {
    if (!place) return null;

    const days = Array.from({ length: VISIBLE_DAYS }, (_, i) => addDays(windowStart, i));
    const allSlots = days.map((date) => {
      const dayName = date.toLocaleDateString("fr-FR", { weekday: "long" });
      return generateSlotsForDay(dayName);
    });

    const maxSlots = Math.max(0, ...allSlots.map((sl) => sl.length));
    const rows = showMore ? maxSlots : Math.min(DEFAULT_ROWS, maxSlots);

    const workHoursToUse = getWorkingHours();
    const activityDurationMin = getActivityDuration();

    const rangeTitle = `${days[0].toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })} au ${days[
      days.length - 1
    ].toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })}`;

    const usePxCols = isApp || IS_IOS;

    const tableStyle = usePxCols
      ? {
          tableLayout: "fixed",
          width: "auto",
          minWidth: FIRST_COL_PX + VISIBLE_DAYS * DAY_COL_PX_APP,
        }
      : {
          tableLayout: "fixed",
          width: "100%",
        };

    const dayColStyle = usePxCols
      ? { width: DAY_COL_PX_APP }
      : { width: `calc((100% - ${FIRST_COL_PX}px) / ${VISIBLE_DAYS})` };

    return (
      <>
        <div className={headCls} role="group" aria-label="Navigation du calendrier">
          <button className={prevBtnCls} onClick={handlePrev} aria-label="Plage précédente">
            ⬅️
          </button>
          <span className="calendar-head__title" style={{ fontSize: 16 }}>
            Du {rangeTitle}
          </span>
          <button className={nextBtnCls} onClick={handleNext} aria-label="Plage suivante">
            ➡️
          </button>
        </div>

        <div className="mini-calendar__tablewrap mini-calendar__tablewrap--compact">
          <table className="mini-calendar-table" aria-label="Disponibilités" style={tableStyle}>
            <colgroup>
              <col style={{ width: FIRST_COL_PX }} />
              {days.map((_, i) => (
                <col key={i} style={dayColStyle} />
              ))}
            </colgroup>

            <thead>
              <tr>
                <th scope="col" className="mini-calendar__corner" style={{ fontSize: 13 }}>
                  Horaires
                </th>
                {days.map((d, i) => (
                  <th key={i} scope="col" style={{ fontSize: 13, lineHeight: 1.2, whiteSpace: "nowrap" }}>
                    {shortDayLabel(d)} {d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {Array.from({ length: rows }).map((_, rowIdx) => (
                <tr key={rowIdx}>
                  <td className="mini-calendar__timecell" style={{ fontWeight: 700 }}>
                    {(() => {
                      const firstWithRow = allSlots.find((sl) => sl[rowIdx]);
                      return firstWithRow ? firstWithRow[rowIdx] : "—";
                    })()}
                  </td>

                  {allSlots.map((slots, dayIdx) => {
                    const slotTime = slots[rowIdx];
                    if (!slotTime) return <td key={dayIdx} className="mini-calendar__empty">—</td>;

                    const date = new Date(days[dayIdx]);
                    const [hour, minute] = safeSplit(slotTime).map((x) => parseIntSafe(x));
                    date.setHours(hour, minute, 0, 0);

                    const slotStart = new Date(date);
                    const slotEnd = new Date(date.getTime() + activityDurationMin * 60000);

                    const dayName = date.toLocaleDateString("fr-FR", { weekday: "long" });
                    const rawWorkDay = workHoursToUse.find((d) =>
                      (d?.day || "").toLowerCase().startsWith(dayName.toLowerCase())
                    );

                    const isPast = isSlotPast(date);
                    const isBooked = bookedSlots.some((b) => slotStart < b.end && slotEnd > b.start);
                    const isBlocked = blockSlots.some((b) => slotStart < b.end && slotEnd > b.start);
                    const isWithinWorkHours = slotIsWithinWorkingHours(slotStart, slotEnd, rawWorkDay);

                    const disabled = isPast || isBooked || isBlocked || !isWithinWorkHours;

                    return (
                      <td key={dayIdx}>
                        <button
                          className={`slot-btn ${isBooked ? "booked" : ""} ${isBlocked ? "blocked" : ""} ${isPast ? "past" : ""}`}
                          disabled={disabled}
                          aria-disabled={disabled}
                          onClick={() => !disabled && handleSlotClick(date)}
                          title={
                            isPast
                              ? "Créneau passé"
                              : isBlocked
                              ? "Indisponible (congé)"
                              : isBooked
                              ? "Déjà réservé"
                              : !isWithinWorkHours
                              ? "Ce créneau dépasse les horaires du professionnel"
                              : "Réserver"
                          }
                          style={{ width: "100%", minWidth: 0, padding: "8px 0", fontSize: 13 }}
                        >
                          {slotTime}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {maxSlots > DEFAULT_ROWS ? (
          <button className={moreBtnCls} onClick={() => setShowMore((v) => !v)}>
            {showMore ? "Réduire les créneaux" : "Voir plus de créneaux"}
          </button>
        ) : null}
      </>
    );
  };

  return (
    <div className={rootCls} style={{ "--first-col": `${FIRST_COL_PX}px` }}>
      {renderCalendar()}
    </div>
  );
}

export default MiniCalendar;

